import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';
import User from 'js/lib/user';

import { mapProgramCurriculumsForCollectionDisplay } from 'bundles/collections-carousel/utils/mappingUtils';
import { SKILLS_IDS_FOR_CLIPS_ALLOW_LIST } from 'bundles/enterprise-legacy-learner-home/constants/skillCollectionsConstants';
import ClipsForSkillsInProgram from 'bundles/enterprise-legacy-learner-home/queries/ClipsForSkillsInProgram.graphql';
import type {
  ClipsForSkillsInProgramQuery,
  ClipsForSkillsInProgramQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ClipsForSkillsInProgram';
import type { DiscoClipsCollections } from 'bundles/enterprise-legacy-learner-home/types/Products';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import { mergeAndMapSkillsClipsCollections } from 'bundles/enterprise-legacy-learner-home/utils/skillsClipsUtils';
import { useEnterpriseOnboardingData } from 'bundles/page-config-common/contexts/enterprise/EnterpriseOnboardingContext';
import EnterpriseSkillsClipsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseSkillClipsContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { MAX_ONBOARDING_SKILLS_LENGTH } from 'bundles/unified-home-common/constants';

import _t from 'i18n!nls/consumer-home';

/**
 * @description This provider is used to fetch and provide skill clips for the learner home page
 */
const EnterpriseSkillsClipsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const program = useProgram();
  const { programSlug } = useParams();
  const { enterpriseOnboardingDataLoading, enterpriseCompletedOnboarding, skills } = useEnterpriseOnboardingData();

  const programId = program?.id;
  const isAuthenticated = User.isAuthenticatedUser();

  const filteredSkills = useMemo(
    () => skills.filter((skill) => SKILLS_IDS_FOR_CLIPS_ALLOW_LIST.includes(skill.id)),
    [skills]
  );

  const skillParams = useMemo(
    () =>
      filteredSkills.slice(0, MAX_ONBOARDING_SKILLS_LENGTH).map((skill, index) => ({
        name: `skill-based-clips-${index + 1}.skillName`,
        value: skill.name,
      })),
    [filteredSkills]
  );

  const { loading, data } = useQuery<ClipsForSkillsInProgramQuery, ClipsForSkillsInProgramQueryVariables>(
    ClipsForSkillsInProgram,
    {
      variables: {
        programId: programId || '',
        skillParams,
        contextId: 'program-home-skill-based-clips',
      },
      skip:
        !isAuthenticated ||
        !programId ||
        enterpriseOnboardingDataLoading ||
        !enterpriseCompletedOnboarding ||
        skillParams.length < 1,
      ssr: false,
      notifyOnNetworkStatusChange: true,
      context: { clientName: 'gatewayGql' },
    }
  );

  if (!programId || enterpriseOnboardingDataLoading || loading) {
    return (
      <EnterpriseSkillsClipsContext.Provider value={{ skillClipsLoading: true, skillClipsCollections: undefined }}>
        {children}
      </EnterpriseSkillsClipsContext.Provider>
    );
  }

  const skillClipsCollections = filterExistsOrDefault(
    data?.DiscoveryCollections?.queryEnterpriseCollectionsByProgram?.productCollections || []
  ) as DiscoClipsCollections | undefined;

  const collection = skillClipsCollections?.length
    ? mergeAndMapSkillsClipsCollections(
        skillClipsCollections,
        _t('Videos recommended for you'),
        'onboarding-clips-collection-program-home'
      )
    : undefined;

  return (
    <EnterpriseSkillsClipsContext.Provider
      value={{
        skillClipsLoading: false,
        skillClipsCollections: collection
          ? // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            mapProgramCurriculumsForCollectionDisplay(programSlug, [collection], [], collection.clips)
          : [],
      }}
    >
      {children}
    </EnterpriseSkillsClipsContext.Provider>
  );
};

export default EnterpriseSkillsClipsProvider;
